/**
 * useBackgroundJobStatus Hook
 * ORCA360 - Real-Time Background Job Status Tracking
 *
 * Polls the sessions table for background job statuses:
 * - Evidence Validation (pilot & instructor)
 * - Root Cause Analysis (RCA)
 * - SMART Goals Generation
 *
 * Used by: SessionDetails, AssessmentGenerator
 * Pattern: Based on useRCAPolling hook
 */

import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Background job status type
 * - verbatim: Span-based extraction used, evidence is verbatim by construction (no validation needed)
 */
export type JobStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped' | 'verbatim';

/**
 * Background job status for a single assessment type (pilot or instructor)
 */
export interface BackgroundJobStatus {
  // Evidence Validation
  validationStatus: JobStatus;
  validationCompletedAt: string | null;
  validationError: string | null;

  // RCA Generation
  rcaStatus: JobStatus;
  rcaCompletedAt: string | null;

  // SMART Goals Generation
  smartStatus: JobStatus;
  smartCompletedAt: string | null;

  // Computed properties
  isAnyProcessing: boolean;
  allCompleted: boolean;
  hasErrors: boolean;
  lastChecked: Date;
}

/**
 * Combined status for both pilot and instructor assessments
 */
export interface CombinedBackgroundJobStatus {
  pilot: BackgroundJobStatus;
  instructor: BackgroundJobStatus;
  isLoading: boolean;
  /** True once the first fetch has completed successfully */
  hasInitiallyLoaded: boolean;
  error: string | null;
  /** Call to force an immediate status refresh (e.g., after inline validation completes) */
  refresh: () => Promise<void>;
}

/**
 * Polling interval (3 seconds)
 */
const POLL_INTERVAL_MS = 3000;

/**
 * Hook to poll background job status for a session
 *
 * @param sessionId - Session ID to track
 * @param enabled - Whether polling is enabled (default: true)
 * @returns Combined status object for pilot and instructor assessments
 */
export function useBackgroundJobStatus(
  sessionId: string | null | undefined,
  enabled: boolean = true
): CombinedBackgroundJobStatus {
  // Default validation to 'verbatim' since span-based extraction is always enabled
  // This prevents brief "Pending" flash before database status loads
  const [pilotStatus, setPilotStatus] = useState<BackgroundJobStatus>({
    validationStatus: 'verbatim',
    validationCompletedAt: null,
    validationError: null,
    rcaStatus: 'pending',
    rcaCompletedAt: null,
    smartStatus: 'pending',
    smartCompletedAt: null,
    isAnyProcessing: false,
    allCompleted: false,
    hasErrors: false,
    lastChecked: new Date(),
  });

  const [instructorStatus, setInstructorStatus] = useState<BackgroundJobStatus>({
    validationStatus: 'verbatim',
    validationCompletedAt: null,
    validationError: null,
    rcaStatus: 'pending',
    rcaCompletedAt: null,
    smartStatus: 'pending',
    smartCompletedAt: null,
    isAnyProcessing: false,
    allCompleted: false,
    hasErrors: false,
    lastChecked: new Date(),
  });

  const [isLoading, setIsLoading] = useState(true);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch current status from database
   */
  const fetchStatus = async () => {
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('sessions')
        .select(`
          validation_status,
          validation_completed_at,
          validation_error,
          rca_generation_status,
          rca_generation_completed_at,
          smart_goals_generation_status,
          smart_goals_generation_completed_at,
          instructor_validation_status,
          instructor_validation_completed_at,
          instructor_validation_error
        `)
        .eq('id', sessionId)
        .single();

      if (fetchError) {
        console.error('[useBackgroundJobStatus] Error fetching status:', fetchError);
        setError(fetchError.message);
        setIsLoading(false);
        return;
      }

      if (!data) {
        setIsLoading(false);
        return;
      }

      // Parse pilot status
      // Span-based extraction is always enabled, so default to 'verbatim' instead of 'pending'
      // Also treat 'pending' from database as 'verbatim' (legacy sessions before span-based extraction)
      const rawPilotValidationStatus = data.validation_status as JobStatus;
      const pilotValidationStatus: JobStatus = 
        (!rawPilotValidationStatus || rawPilotValidationStatus === 'pending') 
          ? 'verbatim' 
          : rawPilotValidationStatus;
      const pilotRcaStatus = (data.rca_generation_status || 'pending') as JobStatus;
      const pilotSmartStatus = (data.smart_goals_generation_status || 'pending') as JobStatus;

      const pilotIsProcessing =
        pilotValidationStatus === 'in_progress' ||
        pilotRcaStatus === 'in_progress' ||
        pilotSmartStatus === 'in_progress';

      const pilotAllComplete =
        (pilotValidationStatus === 'completed' || pilotValidationStatus === 'skipped' || pilotValidationStatus === 'verbatim') &&
        (pilotRcaStatus === 'completed' || pilotRcaStatus === 'skipped') &&
        (pilotSmartStatus === 'completed' || pilotSmartStatus === 'skipped');

      // Stop polling here (inside fetchStatus) so the decision is made on fresh DB data,
      // not on stale React state from the previous effect cycle. Avoids the race where
      // Effect 2 would fire after Effect 1 recreates the interval (e.g., on session navigation).
      // Instructor RCA/SMART are always inline-completed; only check validation
      const instructorAllCompleteEarly =
        ((!data.instructor_validation_status || data.instructor_validation_status === 'pending')
          ? true /* verbatim */ : ['completed', 'skipped', 'verbatim'].includes(data.instructor_validation_status));

      if (pilotAllComplete && instructorAllCompleteEarly) {
        if (intervalRef.current) {
          console.log('[useBackgroundJobStatus] All jobs complete - stopping polling');
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      const pilotHasErrors =
        pilotValidationStatus === 'failed' ||
        pilotRcaStatus === 'failed' ||
        pilotSmartStatus === 'failed';

      setPilotStatus({
        validationStatus: pilotValidationStatus,
        validationCompletedAt: data.validation_completed_at,
        validationError: data.validation_error,
        rcaStatus: pilotRcaStatus,
        rcaCompletedAt: data.rca_generation_completed_at,
        smartStatus: pilotSmartStatus,
        smartCompletedAt: data.smart_goals_generation_completed_at,
        isAnyProcessing: pilotIsProcessing,
        allCompleted: pilotAllComplete,
        hasErrors: pilotHasErrors,
        lastChecked: new Date(),
      });

      // Parse instructor status
      // Same logic: treat pending/null as verbatim since span-based extraction is always enabled
      const rawInstructorValidationStatus = data.instructor_validation_status as JobStatus;
      const instructorValidationStatus: JobStatus = 
        (!rawInstructorValidationStatus || rawInstructorValidationStatus === 'pending') 
          ? 'verbatim' 
          : rawInstructorValidationStatus;
      // Instructor Development Areas (RCA) and Professional Development Goals (SMART)
      // are generated INLINE during instructor assessment — not as background jobs.
      // They must NOT read from the shared pilot RCA/SMART columns, which get reset
      // when a pilot assessment is generated, causing false "Pending" status.
      const instructorRcaStatus: JobStatus = 'completed';
      const instructorSmartStatus: JobStatus = 'completed';

      // Instructor RCA/SMART are always 'completed' (generated inline),
      // so only validation status matters for instructor processing state.
      const instructorIsProcessing =
        instructorValidationStatus === 'in_progress';

      const instructorAllComplete =
        (instructorValidationStatus === 'completed' || instructorValidationStatus === 'skipped' || instructorValidationStatus === 'verbatim');

      const instructorHasErrors =
        instructorValidationStatus === 'failed';

      setInstructorStatus({
        validationStatus: instructorValidationStatus,
        validationCompletedAt: data.instructor_validation_completed_at,
        validationError: data.instructor_validation_error,
        rcaStatus: instructorRcaStatus,
        rcaCompletedAt: data.instructor_validation_completed_at, // Use instructor timestamp (inline generation)
        smartStatus: instructorSmartStatus,
        smartCompletedAt: data.instructor_validation_completed_at, // Use instructor timestamp (inline generation)
        isAnyProcessing: instructorIsProcessing,
        allCompleted: instructorAllComplete,
        hasErrors: instructorHasErrors,
        lastChecked: new Date(),
      });

      setIsLoading(false);
      setHasInitiallyLoaded(true);
      setError(null);
    } catch (err) {
      console.error('[useBackgroundJobStatus] Unexpected error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
    }
  };

  /**
   * Start polling when hook mounts or dependencies change
   */
  useEffect(() => {
    if (!sessionId || !enabled) {
      setIsLoading(false);
      return;
    }

    // Initial fetch
    fetchStatus();

    // Set up polling interval
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [sessionId, enabled]);

  return {
    pilot: pilotStatus,
    instructor: instructorStatus,
    isLoading,
    hasInitiallyLoaded,
    error,
    // Expose refresh function for immediate status update (e.g., after inline validation completes)
    refresh: fetchStatus,
  };
}

/**
 * Get human-readable status message for a job
 */
export function getJobStatusMessage(status: JobStatus, completedAt: string | null): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'in_progress':
      return 'Processing...';
    case 'completed':
      return completedAt
        ? `Completed at ${new Date(completedAt).toLocaleTimeString()}`
        : 'Completed';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped (not applicable)';
    case 'verbatim':
      return 'Verbatim (span-based)';
    default:
      return 'Unknown';
  }
}

/**
 * Get Tailwind color class for job status
 */
export function getJobStatusColor(status: JobStatus): string {
  switch (status) {
    case 'pending':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'in_progress':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'completed':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'verbatim':
      return 'text-green-600 bg-green-50 border-green-200'; // Green like completed
    case 'failed':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'skipped':
      return 'text-gray-500 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

/**
 * Get icon for job status
 */
export function getJobStatusIcon(status: JobStatus): string {
  switch (status) {
    case 'pending':
      return '⏸️';
    case 'in_progress':
      return '⏳';
    case 'completed':
      return '✅';
    case 'verbatim':
      return '✅'; // Checkmark like completed
    case 'failed':
      return '❌';
    case 'skipped':
      return '➖';
    default:
      return '❓';
  }
}
